import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ClipboardList,
  Upload,
  Search,
  Eye,
  CheckCircle,
  Clock,
  AlertTriangle,
  Loader2,
  Trash2,
  RefreshCw,
  FileText,
  Mail,
  ShieldCheck,
  XCircle,
  ExternalLink,
  Minus,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { DashboardLayout } from '../components/DashboardLayout';
import { StatementPayoutMatchingCard } from '../components/admin/StatementPayoutMatchingCard';
import { managementStatementsAPI, entitiesAPI } from '../lib/api';
import { useEntityStore } from '../store';
import { getErrorMessage } from '../lib/errors';

const statusConfig = {
  clean: { label: 'Clean', variant: 'default', icon: CheckCircle, color: 'text-green-600' },
  flagged: { label: 'Flagged', variant: 'destructive', icon: AlertTriangle, color: 'text-red-600' },
  pending: { label: 'Pending', variant: 'secondary', icon: Clock, color: 'text-slate-500' },
};

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  return (
    <Badge variant={config.variant} className="gap-1">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function fmt(amount, currency = 'GBP') {
  if (amount == null || amount === '') return '-';
  const num = Number(amount);
  if (isNaN(num)) return '-';
  const sym = currency === 'EUR' ? '\u20ac' : currency === 'USD' ? '$' : '\u00a3';
  return `${sym}${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function CheckIcon({ passed }) {
  return passed
    ? <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
    : <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />;
}

function SeverityBadge({ severity }) {
  const variants = { error: 'destructive', warning: 'secondary', info: 'outline' };
  return <Badge variant={variants[severity] || 'outline'} className="text-xs">{severity}</Badge>;
}

// A statement reconciled before the ref-index/amount-gate fix (CLAUDE.md
// diagnosis d0bc672f) still stores a bare 'matched'/'unmatched' string in
// booking_match_statuses instead of the new {status, match_basis} shape --
// only upgraded once the statement is recomputed (Re-reconcile button, or
// the Diagnostics bulk recompute Apply). Tolerate both shapes so an
// un-recomputed statement's Match column still renders instead of crashing.
function normalizeMatchEntry(entry) {
  if (entry == null) return null;
  if (typeof entry === 'string') return { status: entry, match_basis: null };
  return entry;
}

const MATCH_BASIS_LABEL = { ref_index: 'via ref', fallback: 'via guest+dates' };

// Mirrors services/statement_reconciler.py: a statement row with no booking
// ref (CWC leaves it blank on direct bookings) is keyed "guest_name|arrival".
// Looking it up by the empty ref showed matched direct bookings as
// "Not reconciled" (Fan, 2026-10-15, Marcello Barbato, Castle St Aug).
function matchKeyFor(b) {
  const ref = (b.booking_ref || '').trim();
  return ref || `${b.guest_name || ''}|${b.arrival || ''}`;
}

function BookingMatchBadge({ entry }) {
  const status = entry?.status;
  const basisLabel = entry?.match_basis ? MATCH_BASIS_LABEL[entry.match_basis] : null;

  if (status === 'matched') {
    return (
      <span className="inline-flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
          <CheckCircle className="h-3 w-3" />
          Reconciled
        </span>
        {basisLabel && <span className="text-[10px] text-muted-foreground">{basisLabel}</span>}
      </span>
    );
  }
  if (status === 'ref_matched_amount_mismatch') {
    return (
      <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium">
        <AlertTriangle className="h-3 w-3" />
        Amount mismatch
      </span>
    );
  }
  if (status === 'unmatched') {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
        <XCircle className="h-3 w-3" />
        Not reconciled
      </span>
    );
  }
  // Completeness did not run (property unmapped, etc.)
  return (
    <span className="inline-flex items-center gap-1 text-slate-400 text-xs">
      <Minus className="h-3 w-3" />
      Not checked
    </span>
  );
}


// ── Reconciliation Checklist ──

function ReconciliationChecklist({ reconciliation }) {
  if (!reconciliation) return <p className="text-muted-foreground text-sm">Not yet reconciled</p>;
  const { status, checks = [], summary = {} } = reconciliation;
  const failedChecks = checks.filter((c) => !c.passed);
  const passedChecks = checks.filter((c) => c.passed);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <StatusBadge status={status} />
        <span className="text-sm text-muted-foreground">
          {summary.passed}/{summary.total_checks} checks passed
          {summary.errors > 0 && <span className="text-red-600 ml-2">{summary.errors} error(s)</span>}
          {summary.warnings > 0 && <span className="text-amber-600 ml-2">{summary.warnings} warning(s)</span>}
        </span>
      </div>

      {failedChecks.length > 0 && (
        <div className="space-y-2">
          <h5 className="font-medium text-red-700 dark:text-red-400 text-sm">Failed Checks</h5>
          {failedChecks.map((c, i) => (
            <div key={i} className="flex items-start gap-2 p-2 rounded bg-red-50 dark:bg-red-950 text-sm">
              <CheckIcon passed={false} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{c.name}</span>
                  <SeverityBadge severity={c.severity} />
                </div>
                <p className="text-muted-foreground text-xs mt-0.5">{c.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {passedChecks.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            {passedChecks.length} passed check(s)
          </summary>
          <div className="space-y-1 mt-2">
            {passedChecks.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckIcon passed={true} />
                <span>{c.name}: {c.detail}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}


// ── Detail Dialog Content ──

function StatementDetail({ stmt, onReconcile, isReconciling, onDownloadPdf }) {
  if (!stmt) return null;
  const bookings = stmt.bookings || [];
  const expenses = stmt.expenses || [];
  const totals = stmt.totals || {};
  const payment = stmt.payment_summary || {};
  const recon = stmt.reconciliation;

  return (
    <div className="space-y-6">
      {/* Identity */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <div><span className="text-muted-foreground">Agency:</span> {stmt.agency}</div>
        <div><span className="text-muted-foreground">Landlord:</span> {stmt.landlord_entity}</div>
        <div><span className="text-muted-foreground">Property:</span> {stmt.property_name_raw}</div>
        <div><span className="text-muted-foreground">Address:</span> {stmt.property_address_raw}</div>
        <div><span className="text-muted-foreground">Month:</span> {stmt.statement_month}</div>
        <div><span className="text-muted-foreground">Date:</span> {stmt.statement_date}</div>
        <div>
          <span className="text-muted-foreground">Property Mapped:</span>{' '}
          {stmt.property_ref
            ? <Badge variant="outline" className="text-green-600">Yes</Badge>
            : <Badge variant="destructive">Unmapped</Badge>}
        </div>
        {stmt.source === 'gmail' && stmt.source_email_id && (
          <div>
            <button
              type="button"
              onClick={() => onDownloadPdf(stmt.statement_id, stmt.filename)}
              className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm"
            >
              <FileText className="h-3 w-3" />
              Source PDF
            </button>
          </div>
        )}
        {stmt.drive_save_status === 'saved' && stmt.drive_web_link && (
          <div>
            <a
              href={stmt.drive_web_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm"
            >
              <FileText className="h-3 w-3" />
              View in Drive
            </a>
          </div>
        )}
        {stmt.drive_save_status === 'failed' && (
          <div className="col-span-2">
            <span className="inline-flex items-center gap-1 text-orange-600 text-sm">
              <AlertTriangle className="h-3 w-3" />
              Drive save failed
            </span>
            {stmt.drive_save_error && (
              <p className="text-xs text-muted-foreground mt-1 break-words">{stmt.drive_save_error}</p>
            )}
          </div>
        )}
      </div>

      {/* Totals Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          ['Gross Income', totals.income_gross, ''],
          ['Commission', totals.total_commission, 'text-red-600'],
          ['Payment Charges', totals.total_payment_charges, 'text-orange-600'],
          ['Net Income', totals.income_net, 'text-blue-600'],
          ['Total Expenses', totals.total_expenses, 'text-red-600'],
          ['Net Profit', totals.net_profit, 'text-green-600 font-bold'],
        ].map(([label, val, cls]) => (
          <div key={label} className="p-3 rounded-lg bg-muted">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className={`text-lg font-semibold ${cls}`}>{fmt(val)}</div>
          </div>
        ))}
      </div>

      {/* Bookings Table */}
      {bookings.length > 0 && (
        <div>
          <h4 className="font-semibold mb-2">Bookings ({bookings.length})</h4>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Guest</TableHead>
                  <TableHead>Arrival</TableHead>
                  <TableHead>Departure</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                  <TableHead className="text-right">Charges</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead>Match</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.map((b, i) => {
                  const matchStatuses = recon?.booking_match_statuses;
                  const matchEntry = matchStatuses
                    ? normalizeMatchEntry(matchStatuses[matchKeyFor(b)]) || { status: 'unmatched', match_basis: null }
                    : null;
                  return (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{b.booking_ref}</TableCell>
                      <TableCell>{b.guest_name}</TableCell>
                      <TableCell>{b.arrival}</TableCell>
                      <TableCell>{b.departure}</TableCell>
                      <TableCell><Badge variant="outline">{b.channel}</Badge></TableCell>
                      <TableCell className="text-right">{fmt(b.gross)}</TableCell>
                      <TableCell className="text-right text-red-600">{fmt(b.commission)}</TableCell>
                      <TableCell className="text-right text-orange-600">{fmt(b.payment_charges)}</TableCell>
                      <TableCell className="text-right font-medium">{fmt(b.net)}</TableCell>
                      <TableCell><BookingMatchBadge entry={matchEntry} /></TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals row */}
                <TableRow className="border-t-2 font-bold">
                  <TableCell colSpan={5}>Totals</TableCell>
                  <TableCell className="text-right">{fmt(totals.income_gross)}</TableCell>
                  <TableCell className="text-right text-red-600">{fmt(totals.total_commission)}</TableCell>
                  <TableCell className="text-right text-orange-600">{fmt(totals.total_payment_charges)}</TableCell>
                  <TableCell className="text-right">{fmt(totals.income_net)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Expenses Table */}
      {expenses.length > 0 && (
        <div>
          <h4 className="font-semibold mb-2">Expenses ({expenses.length})</h4>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Supplier</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Account</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.map((e, i) => (
                <TableRow key={i}>
                  <TableCell>{e.supplier}</TableCell>
                  <TableCell>{e.description}</TableCell>
                  <TableCell>{e.account}</TableCell>
                  <TableCell className="text-right text-red-600">{fmt(e.amount)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2 font-bold">
                <TableCell colSpan={3}>Total Expenses</TableCell>
                <TableCell className="text-right text-red-600">{fmt(totals.total_expenses)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      {/* Payment Summary */}
      {(payment.initial_collection || payment.first_payment) && (
        <div>
          <h4 className="font-semibold mb-2">Payment Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="p-2 rounded bg-muted">
              <div className="text-xs text-muted-foreground">Initial Collection</div>
              <div className="font-medium">{fmt(payment.initial_collection)}</div>
            </div>
            <div className="p-2 rounded bg-muted">
              <div className="text-xs text-muted-foreground">First Payment</div>
              <div className="font-medium">{fmt(payment.first_payment)}</div>
            </div>
            <div className="p-2 rounded bg-muted">
              <div className="text-xs text-muted-foreground">Balance</div>
              <div className="font-medium">{fmt(payment.balance)}</div>
            </div>
            <div className="p-2 rounded bg-muted">
              <div className="text-xs text-muted-foreground">Remainder</div>
              <div className="font-medium">{fmt(payment.remainder)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Reconciliation Checklist */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Reconciliation
          </h4>
          <Button
            variant="outline"
            size="sm"
            onClick={onReconcile}
            disabled={isReconciling}
          >
            {isReconciling
              ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Reconciling...</>
              : <><RefreshCw className="h-3 w-3 mr-1" />Re-reconcile</>}
          </Button>
        </div>
        <ReconciliationChecklist reconciliation={recon} />
      </div>
    </div>
  );
}


// ── Main Page ──

export function ManagementStatementsPage() {
  const [searchParams] = useSearchParams();
  const [statements, setStatements] = useState([]);
  const [entities, setEntities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadEntity, setUploadEntity] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [scanDiagnostics, setScanDiagnostics] = useState(null);
  const [scopeInfo, setScopeInfo] = useState(null);
  const [parseFailures, setParseFailures] = useState([]);
  const [showParseFailures, setShowParseFailures] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [hardDeleteRefusal, setHardDeleteRefusal] = useState(null);
  const { selectedEntity, selectedScope } = useEntityStore();
  const isPortfolio = selectedScope === 'portfolio';

  const fetchStatements = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {};
      if (isPortfolio) {
        params.entity_id = 'property_portfolio';
      } else if (selectedEntity && selectedEntity !== 'all') {
        params.entity_id = selectedEntity;
      }
      const { data } = await managementStatementsAPI.getAll(params);
      setStatements(data.statements || []);
      setScopeInfo(data.scope || null);
    } catch (error) {
      console.error('Failed to fetch statements:', error);
      toast.error('Failed to load management statements');
    } finally {
      setIsLoading(false);
    }
  }, [selectedEntity, isPortfolio]);

  const fetchDiagnostics = useCallback(() => {
    managementStatementsAPI.scanDiagnostics()
      .then(({ data }) => setScanDiagnostics(data))
      .catch(() => {});
  }, []);

  const fetchParseFailures = useCallback(() => {
    managementStatementsAPI.getParseFailures()
      .then(({ data }) => setParseFailures(data.failures || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchStatements();
    fetchDiagnostics();
    fetchParseFailures();
    entitiesAPI.getAll().then(({ data }) => setEntities(data || [])).catch(() => {});
  }, [fetchStatements, fetchDiagnostics, fetchParseFailures]);

  // Deep-link from the Bank Transactions "Reconciled By" column: ?statement_id=
  // opens that statement's detail modal once the list has loaded.
  useEffect(() => {
    const targetId = searchParams.get('statement_id');
    if (!targetId || statements.length === 0) return;
    const target = statements.find((s) => s.statement_id === targetId);
    if (target) setShowDetail(target);
  }, [searchParams, statements]);

  // Poll scan progress while scanning
  useEffect(() => {
    if (!isScanning) {
      setScanProgress(null);
      return;
    }
    const interval = setInterval(() => {
      managementStatementsAPI.scanProgress()
        .then(({ data }) => { if (data.active) setScanProgress(data); })
        .catch(() => {});
    }, 2000);
    return () => clearInterval(interval);
  }, [isScanning]);

  const handleScanGmail = async (forceRescan = false) => {
    setIsScanning(true);
    setScanProgress(null);
    try {
      const { data } = await managementStatementsAPI.scanGmail({ forceRescan });
      if (data.statements_created > 0) {
        toast.success(`Found ${data.statements_created} new management statement(s)`);
        await fetchStatements();
      } else if (data.statements_found > 0) {
        toast.info('No new statements, all emails already processed');
      } else {
        toast.info(`No management statement emails found${!forceRescan ? '. Try "Force rescan all".' : '.'}`);
      }
      if (data.scan_errors?.length) {
        data.scan_errors.forEach(err => toast.error(`Scan defect: ${err}`));
      }
      if (data.failed_emails?.length) {
        data.failed_emails.forEach(fe => {
          const label = fe.subject || fe.filename || fe.email_id || 'unknown';
          toast.warning(`Parse failed: ${label}: ${fe.error}`);
        });
      } else if (data.errors > 0) {
        toast.warning(`${data.errors} email(s) failed to parse`);
      }
      fetchDiagnostics();
      fetchParseFailures();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gmail scan failed');
    } finally {
      setIsScanning(false);
      setScanProgress(null);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a PDF file');
      return;
    }
    try {
      setIsUploading(true);
      const { data } = await managementStatementsAPI.upload(uploadFile, uploadEntity || undefined);
      const recon = data.statement?.reconciliation;
      const statusMsg = recon ? ` (${recon.status})` : '';
      toast.success(`Statement parsed and reconciled${statusMsg}`);
      setShowUpload(false);
      setUploadFile(null);
      setUploadEntity('');
      await fetchStatements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleReconcile = async (statementId) => {
    try {
      setIsReconciling(true);
      const { data } = await managementStatementsAPI.reconcile(statementId);
      toast.success(`Reconciliation: ${data.status} (${data.summary?.passed}/${data.summary?.total_checks} checks passed)`);
      await fetchStatements();
      // Refresh detail if open
      if (showDetail?.statement_id === statementId) {
        const { data: updated } = await managementStatementsAPI.get(statementId);
        setShowDetail(updated);
      }
    } catch (error) {
      toast.error('Reconciliation failed');
    } finally {
      setIsReconciling(false);
    }
  };

  const handleDelete = async (statementId) => {
    try {
      await managementStatementsAPI.delete(statementId);
      toast.success('Statement removed from view');
      if (showDetail?.statement_id === statementId) setShowDetail(null);
      await fetchStatements();
    } catch (error) {
      toast.error('Delete failed');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleHardDelete = async (statementId) => {
    try {
      await managementStatementsAPI.hardDelete(statementId);
      toast.success('Statement permanently deleted');
      if (showDetail?.statement_id === statementId) setShowDetail(null);
      setHardDeleteTarget(null);
      await fetchStatements();
    } catch (error) {
      setHardDeleteTarget(null);
      setHardDeleteRefusal(
        error?.response?.status === 409
          ? getErrorMessage(error)
          : 'Hard delete failed.'
      );
    }
  };

  const handleViewDetail = async (statementId) => {
    try {
      const { data } = await managementStatementsAPI.get(statementId);
      setShowDetail(data);
    } catch (error) {
      toast.error('Failed to load statement details');
    }
  };

  const handleDownloadPdf = async (statementId, filename) => {
    try {
      const { data } = await managementStatementsAPI.downloadAttachment(statementId);
      const url = URL.createObjectURL(data);
      window.open(url, '_blank');
      // Clean up after a delay to let the browser tab load
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (error) {
      toast.error('Failed to download PDF attachment');
    }
  };

  // Filter and sort: flagged first
  const filtered = statements
    .filter((s) => {
      if (filterStatus !== 'all') {
        const reconStatus = s.reconciliation?.status || 'pending';
        if (reconStatus !== filterStatus) return false;
      }
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          (s.property_name_raw || '').toLowerCase().includes(term) ||
          (s.agency || '').toLowerCase().includes(term) ||
          (s.landlord_entity || '').toLowerCase().includes(term) ||
          (s.statement_month || '').includes(term)
        );
      }
      return true;
    })
    .sort((a, b) => {
      // Flagged first, then by date descending
      const aFlagged = a.reconciliation?.status === 'flagged' ? 0 : 1;
      const bFlagged = b.reconciliation?.status === 'flagged' ? 0 : 1;
      if (aFlagged !== bFlagged) return aFlagged - bFlagged;
      return (b.statement_month || '').localeCompare(a.statement_month || '');
    });

  // Summary stats
  const cleanCount = statements.filter((s) => s.reconciliation?.status === 'clean').length;
  const flaggedCount = statements.filter((s) => s.reconciliation?.status === 'flagged').length;
  const pendingCount = statements.filter((s) => !s.reconciliation).length;

  // Cleaning verification aggregate counts (from stamped per-statement fields).
  const cleaningUnparseableCount = statements.reduce(
    (n, s) => n + (s.cleaning_verification?.unparseable_count || 0), 0);
  const cleaningNoCheckoutCount = statements.reduce(
    (n, s) => n + (s.cleaning_verification?.no_checkout_count || 0), 0);
  const cleaningAmbiguousCount = statements.reduce(
    (n, s) => n + (s.cleaning_verification?.ambiguous_count || 0), 0);
  const hasCleaningIssues = cleaningUnparseableCount + cleaningNoCheckoutCount + cleaningAmbiguousCount > 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Management Statements</h1>
            <p className="text-muted-foreground">
              Agency owner statements with automated arithmetic reconciliation
            </p>
            <p className="text-sm font-medium mt-1">
              {scopeInfo?.type === 'property_portfolio'
                ? `Showing statements for: Property Portfolio (${scopeInfo.entity_count} landlord entit${scopeInfo.entity_count === 1 ? 'y' : 'ies'})`
                : scopeInfo?.type === 'single_entity'
                  ? `Showing statements for: ${scopeInfo.entity_name}`
                  : 'Showing statements for: All entities'}
            </p>
          </div>
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={isScanning}>
                  {isScanning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  {isScanning
                    ? (scanProgress?.total
                      ? `${scanProgress.processed || 0} / ${scanProgress.total} processed`
                      : 'Scanning...')
                    : 'Scan Gmail'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleScanGmail(false)}>
                  <Mail className="h-4 w-4 mr-2" />
                  Scan new emails
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleScanGmail(true)}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Force rescan all
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Dialog open={showUpload} onOpenChange={setShowUpload}>
              <DialogTrigger asChild>
                <Button>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Statement
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload Management Statement</DialogTitle>
                  <DialogDescription>
                    Upload a PDF. It will be parsed and reconciled automatically.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label>PDF File</Label>
                    <Input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Entity (optional override)</Label>
                    <Select value={uploadEntity} onValueChange={setUploadEntity}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Auto-detect from document" />
                      </SelectTrigger>
                      <SelectContent>
                        {entities.map((e) => (
                          <SelectItem key={e.id} value={e.type || e.id}>
                            {e.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleUpload} disabled={isUploading || !uploadFile} className="w-full">
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Parsing & Reconciling...
                      </>
                    ) : (
                      'Upload, Parse & Reconcile'
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Scan Diagnostics */}
        {scanDiagnostics && (
          <Card className="bg-muted/40">
            <CardContent className="py-3 px-4">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                <span className="font-medium text-muted-foreground">Gmail Scan</span>
                {scanDiagnostics.last_scan ? (
                  <>
                    <span>
                      Last: {(() => {
                        const ts = scanDiagnostics.last_scan.timestamp;
                        if (!ts) return 'never';
                        const diff = Date.now() - new Date(ts).getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 1) return 'just now';
                        if (mins < 60) return `${mins}m ago`;
                        const hours = Math.floor(mins / 60);
                        if (hours < 24) return `${hours}h ago`;
                        return `${Math.floor(hours / 24)}d ago`;
                      })()}
                      {scanDiagnostics.last_scan.scheduled ? ' (scheduled)' : ' (manual)'}
                    </span>
                    <span>
                      Parsed: {scanDiagnostics.last_scan.statements_created || 0}
                    </span>
                    {(scanDiagnostics.last_scan.errors > 0 || scanDiagnostics.last_scan.scan_errors?.length > 0) && (
                      <span className="text-red-600">
                        Failed: {scanDiagnostics.last_scan.errors || 0}
                        {scanDiagnostics.last_scan.scan_errors?.length > 0 &&
                          ` + ${scanDiagnostics.last_scan.scan_errors.length} defect(s)`}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground">No scans recorded</span>
                )}
                <span className={scanDiagnostics.waiting_emails > 0 ? 'font-medium text-amber-600' : 'text-muted-foreground'}>
                  Waiting: {scanDiagnostics.waiting_emails} email{scanDiagnostics.waiting_emails !== 1 ? 's' : ''}
                </span>
              </div>
              {scanDiagnostics.last_scan?.failed_emails?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {scanDiagnostics.last_scan.failed_emails.map((fe, i) => (
                    <div key={i} className="text-xs text-red-600 bg-red-50 dark:bg-red-950/30 rounded px-2 py-1">
                      <span className="font-medium">{fe.subject || fe.filename || fe.email_id}</span>
                      {fe.from && <span className="text-muted-foreground ml-1">from {fe.from}</span>}
                      <span className="block text-red-500 mt-0.5 break-words">{fe.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* CWC statement-payout reconciliation: Consistency Census + Preview/Apply */}
        <StatementPayoutMatchingCard />

        {/* Parse-failed statements: visible here, not just a count on an
            admin diagnostics card -- Fan reviews statements on this page. */}
        {parseFailures.length > 0 && (
          <Card className="border-red-200 dark:border-red-900" data-testid="parse-failures-card">
            <CardHeader className="pb-2">
              <button
                type="button"
                onClick={() => setShowParseFailures((v) => !v)}
                className="flex items-center gap-2 text-left"
                data-testid="parse-failures-toggle"
              >
                <XCircle className="h-4 w-4 text-red-600" />
                <CardTitle className="text-base text-red-700 dark:text-red-400">
                  {parseFailures.length} statement{parseFailures.length === 1 ? '' : 's'} could not be parsed
                </CardTitle>
              </button>
              <p className="text-xs text-muted-foreground">
                No PDF found, the source message could no longer be fetched, or the parser
                failed on the document. Never silently dropped -- each stays here, with its PDF
                filed where one was recovered, until it is re-ingested successfully or reviewed.
              </p>
            </CardHeader>
            {showParseFailures && (
              <CardContent className="space-y-2">
                {parseFailures.map((f) => (
                  <div
                    key={`${f.source}:${f.source_key}`}
                    className="border rounded p-2 text-sm"
                    data-testid="parse-failure-row"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="destructive">{f.failing_stage}</Badge>
                      <span className="font-medium">{f.email_subject || '(no subject)'}</span>
                      {f.email_date && <span className="text-xs text-muted-foreground">{f.email_date}</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{f.email_from}</p>
                    <p className="text-xs mt-1">{f.error_detail}</p>
                    {f.drive_web_link && (
                      <a
                        href={f.drive_web_link}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
                      >
                        <ExternalLink className="h-3 w-3" />
                        View filed PDF
                      </a>
                    )}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{statements.length}</div>
              <p className="text-xs text-muted-foreground">Total Statements</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{cleanCount}</div>
              <p className="text-xs text-muted-foreground">Clean</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-red-600">{flaggedCount}</div>
              <p className="text-xs text-muted-foreground">Flagged</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-slate-500">{pendingCount}</div>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
        </div>

        {/* Cleaning verification data-quality */}
        {hasCleaningIssues && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-red-200 dark:border-red-900">
              <CardContent className="pt-4 pb-3">
                <div className="text-xl font-bold text-red-500">{cleaningUnparseableCount}</div>
                <p className="text-xs text-muted-foreground">Unparseable cleaning lines</p>
              </CardContent>
            </Card>
            <Card className="border-yellow-200 dark:border-yellow-900">
              <CardContent className="pt-4 pb-3">
                <div className="text-xl font-bold text-yellow-600">{cleaningNoCheckoutCount}</div>
                <p className="text-xs text-muted-foreground">Cleans with no checkout</p>
              </CardContent>
            </Card>
            <Card className="border-orange-200 dark:border-orange-900">
              <CardContent className="pt-4 pb-3">
                <div className="text-xl font-bold text-orange-500">{cleaningAmbiguousCount}</div>
                <p className="text-xs text-muted-foreground">Ambiguous clean matches</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search property, agency, month..."
              className="pl-9 w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="clean">Clean</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchStatements}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <ClipboardList className="h-12 w-12 mb-4 opacity-50" />
                <p>{isPortfolio
                  ? 'No statements for any portfolio property'
                  : 'No management statements found'}</p>
                {!isPortfolio && (
                  <p className="text-sm mt-1">Upload a statement PDF or scan Gmail to get started</p>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    {isPortfolio && <TableHead>Landlord</TableHead>}
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Net Income</TableHead>
                    <TableHead className="text-right">Expenses</TableHead>
                    <TableHead className="text-right">Net Profit</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>PDF</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((stmt) => {
                    const t = stmt.totals || {};
                    const recon = stmt.reconciliation;
                    const reconStatus = recon?.status || 'pending';
                    return (
                      <TableRow key={stmt.statement_id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{stmt.property_name_raw || 'Unknown'}</div>
                            <div className="text-xs text-muted-foreground">{stmt.agency}</div>
                          </div>
                        </TableCell>
                        {isPortfolio && (
                          <TableCell className="text-sm">
                            {stmt.landlord_entity || stmt.landlord_entity_id || '-'}
                          </TableCell>
                        )}
                        <TableCell className="font-mono text-sm">{stmt.statement_month}</TableCell>
                        <TableCell className="text-right">{fmt(t.income_gross)}</TableCell>
                        <TableCell className="text-right text-blue-600">{fmt(t.income_net)}</TableCell>
                        <TableCell className="text-right text-red-600">{fmt(t.total_expenses)}</TableCell>
                        <TableCell className="text-right font-bold text-green-600">{fmt(t.net_profit)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <StatusBadge status={reconStatus} />
                            {stmt.payment_status === 'paid' && (
                              <Badge
                                className="text-[10px] px-1 py-0 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-0"
                                title={stmt.paid_at ? `Paid ${stmt.paid_at}` : 'Paid'}
                              >
                                Paid
                              </Badge>
                            )}
                            {stmt.payment_status === 'partially_paid' && (
                              <Badge
                                className="text-[10px] px-1 py-0 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0"
                                title={`Part-paid${stmt.amount_paid ? `: ${stmt.amount_paid} received` : ''}`}
                              >
                                Part-paid
                              </Badge>
                            )}
                            {/* CWC paid an amount that does not equal this
                                statement's net profit. The pair is settled, and
                                the difference is shown here so it can be chased
                                rather than left invisible. */}
                            {stmt.payout_variance_direction && (
                              <Badge
                                className={`text-[10px] px-1 py-0 border-0 ${
                                  stmt.payout_variance_direction === 'exact'
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                                    : stmt.payout_variance_direction === 'overpayment'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                                }`}
                                title={`Expected ${stmt.payout_expected_amount ?? '?'}, received ${
                                  stmt.payment_source?.amount ?? '?'
                                }`}
                                data-testid={`payout-variance-${stmt.statement_id}`}
                              >
                                {stmt.payout_variance_direction === 'exact'
                                  ? 'Exact'
                                  : `${
                                      stmt.payout_variance_direction === 'overpayment' ? 'Overpaid' : 'Underpaid'
                                    } ${stmt.payout_variance}`}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {stmt.drive_save_status === 'saved' && stmt.drive_web_link ? (
                            <a
                              href={stmt.drive_web_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs"
                              title="View PDF in Drive"
                            >
                              <ExternalLink className="h-3 w-3" />
                              PDF
                            </a>
                          ) : stmt.source === 'gmail' && stmt.source_email_id ? (
                            <button
                              type="button"
                              onClick={() => handleDownloadPdf(stmt.statement_id, stmt.filename)}
                              className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs"
                              title="Download source PDF"
                            >
                              <FileText className="h-3 w-3" />
                              PDF
                            </button>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewDetail(stmt.statement_id)}
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteTarget(stmt)}
                              title="Remove from view"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setHardDeleteTarget(stmt)}
                              title="Hard delete (admin, irreversible)"
                            >
                              <AlertTriangle className="h-4 w-4 text-red-700" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={!!showDetail} onOpenChange={(open) => !open && setShowDetail(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            {showDetail && (
              <>
                <DialogHeader>
                  <DialogTitle>{showDetail.property_name_raw || 'Statement Details'}</DialogTitle>
                  <DialogDescription>
                    {showDetail.agency} / {showDetail.statement_month}
                  </DialogDescription>
                </DialogHeader>
                <StatementDetail
                  stmt={showDetail}
                  onReconcile={() => handleReconcile(showDetail.statement_id)}
                  isReconciling={isReconciling}
                  onDownloadPdf={handleDownloadPdf}
                />
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete (soft-exclude) confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Remove "{deleteTarget?.property_name_raw || deleteTarget?.statement_id}" from view?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This hides the statement from all lists and totals -- it is not deleted. Any bank
                transaction matches pointing at it stay recoverable via the Diagnostics page's
                Statement Orphan Repoint card.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => handleDelete(deleteTarget.statement_id)}>
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Hard delete (admin, irreversible) confirmation */}
        <AlertDialog open={!!hardDeleteTarget} onOpenChange={(open) => { if (!open) setHardDeleteTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Permanently delete "{hardDeleteTarget?.property_name_raw || hardDeleteTarget?.statement_id}"?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This cannot be undone. Refused if any bank transaction match still references this
                statement -- re-point those first via the Diagnostics page's Statement Orphan Repoint
                card.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleHardDelete(hardDeleteTarget.statement_id)}
                className="bg-red-700 hover:bg-red-800"
              >
                Permanently Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Hard delete refusal (409: still referenced) */}
        <AlertDialog open={!!hardDeleteRefusal} onOpenChange={(open) => { if (!open) setHardDeleteRefusal(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cannot delete -- still referenced</AlertDialogTitle>
              <AlertDialogDescription>{hardDeleteRefusal}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setHardDeleteRefusal(null)}>OK</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
